import React from 'react';
import { Screen } from '../enums/screen.enum';
import { useTranslation, Trans } from 'react-i18next';
import ContentContainer from '../partials/ContentContainer';
import ActionsContainer from '../partials/ActionsContainer';
import { Button, ButtonGroup, ButtonVariant } from '../components/Button';
import ScreenContainer from '../partials/ScreenContainer';
import { useLocalStorage } from '@uidotdev/usehooks';
import { Constat } from '../models/Constat';
import { ConstatUtilsService } from '../services/constat-utils.service';
import { Vehicle } from '../models/Vehicle';
import VehicleContainer, {
  VehicleContainerVariant,
} from '../partials/VehicleContainer';
import Illustration from '../components/Illustration';
import InsuredImage from '../assets/images/content/person.png';
import { Form } from '../components/Form';
import { InputField, InputFieldType } from '../components/InputField';

export default function Step2InsuredScreen({
  isActive,
  setScreen,
}: {
  isActive: boolean;
  setScreen: (screen: Screen) => void;
}) {
  const { t } = useTranslation();

  const constatUtilsService = new ConstatUtilsService();

  const [constat, setConstat] = useLocalStorage('constat', new Constat());
  const [emailValid, setEmailValid] = React.useState(
    !constat?.vehicleA?.driverEmail ||
      constatUtilsService.validEmail(constat?.vehicleA?.driverEmail),
  );
  const [phoneValid, setPhoneValid] = React.useState(
    !constat?.vehicleA?.driverTel ||
      constatUtilsService.validCellphone(constat?.vehicleA?.driverTel),
  );

  const handleChange = (fieldName: string, value: any) => {
    setConstat((prevConstat) => {
      const newConstat = new Constat(prevConstat);
      if (!newConstat.vehicleB) {
        newConstat.vehicleB = new Vehicle();
      }
      newConstat.vehicleB.setField(fieldName, value);
      return newConstat;
    });
  };

  return !isActive ? (
    <></>
  ) : (
    <ScreenContainer
      title={
        <Trans i18nKey={'screens.step2-insured.title'}>
          <strong></strong>
        </Trans>
      }
      timelineStep={2}
      extraCssClasses={['screen-step', 'step2-insured']}
    >
      <ContentContainer>
        <h3>{t('screens.step2-insured.instructions')}</h3>
        <VehicleContainer variant={VehicleContainerVariant.B}>
          <h2>
            <Illustration.Image src={InsuredImage} alt=""></Illustration.Image>
            {t('screens.step2-insured.second-title')}
          </h2>
        </VehicleContainer>
        <Form
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
          }}
        >
          <InputField
            name="insuredName"
            id="insuredName"
            type={InputFieldType.Text}
            required={true}
            label={t('vehicle.label.insuredName')}
            value={constat.vehicleB?.insuredName || ''}
            onChange={(value) => handleChange('insuredName', value)}
          />
          <InputField
            name="insuredFirstname"
            id="insuredFirstname"
            type={InputFieldType.Text}
            required={true}
            label={t('vehicle.label.insuredFirstname')}
            value={constat.vehicleB?.insuredFirstname || ''}
            onChange={(value) => handleChange('insuredFirstname', value)}
          />
          <InputField
            name="insuredTel"
            id="insuredTel"
            type={InputFieldType.Phone}
            required={true}
            label={t('vehicle.label.insuredTel')}
            value={constat.vehicleB?.insuredTel || ''}
            setValid={setPhoneValid}
            validateWith={constatUtilsService.validCellphone}
            onChange={(value) => handleChange('insuredTel', value)}
          />
          <InputField
            name="insuredEmail"
            id="insuredEmail"
            type={InputFieldType.Email}
            required={true}
            label={t('vehicle.label.insuredEmail')}
            value={constat.vehicleB?.insuredEmail || ''}
            setValid={setEmailValid}
            validateWith={constatUtilsService.validEmail}
            onChange={(value) => handleChange('insuredEmail', value)}
          />
        </Form>
      </ContentContainer>

      <ActionsContainer>
        <ButtonGroup>
          <Button
            variant={ButtonVariant.Green}
            isOutlined
            onClick={() => setScreen(Screen.step2Driver)}
          >
            {t('common.previous')}
          </Button>

          <Button
            variant={ButtonVariant.Green}
            isDisabled={
              !emailValid ||
              !phoneValid ||
              !constatUtilsService.isVehicleInsuredComplete(
                new Vehicle(constat.vehicleB),
              )
            }
            onClick={() => setScreen(Screen.step2Vehicle)}
          >
            {t('common.next')}
          </Button>
        </ButtonGroup>
      </ActionsContainer>
    </ScreenContainer>
  );
}
